import React, { useState } from 'react';
import { Layers, FileText, Sparkles, ChevronRight } from 'lucide-react';
import PromptBuilderEnhanced from './PromptBuilderEnhanced';
import PromptBuilderAdvanced from './PromptBuilderAdvanced';

const UnifiedPromptBuilder: React.FC = () => {
  const [activeMode, setActiveMode] = useState<'templates' | 'custom'>('templates');

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header with Mode Switcher */}
      <div className="mb-8">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Prompt Builder
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Create stunning prompts using templates or build from scratch
          </p>
        </div>

        {/* Mode Switcher */}
        <div className="flex justify-center">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-1 inline-flex">
            <button
              onClick={() => setActiveMode('templates')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                activeMode === 'templates'
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
            >
              <FileText className="w-4 h-4" />
              <span>Start with Templates</span>
              {activeMode === 'templates' && (
                <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                  Recommended
                </span>
              )}
            </button>

            <button
              onClick={() => setActiveMode('custom')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                activeMode === 'custom'
                  ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
            >
              <Layers className="w-4 h-4" />
              <span>Build from Scratch</span>
              {activeMode === 'custom' && (
                <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                  Advanced
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mode Description */}
        <div className="mt-4 text-center">
          {activeMode === 'templates' ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4 text-yellow-500" />
              Perfect for beginners • 50+ pre-built templates • Quick customization
            </p>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-500" />
              Full control • Advanced parameters • Professional features
            </p>
          )}
        </div>
      </div>

      {/* Quick Switch Tips */}
      <div className="mb-6 flex justify-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-lg text-sm">
          <span>💡 Tip:</span>
          <span>
            {activeMode === 'templates' 
              ? "Start here to quickly generate prompts with proven templates"
              : "Use this mode for complete control over every aspect of your prompt"}
          </span>
          <button
            onClick={() => setActiveMode(activeMode === 'templates' ? 'custom' : 'templates')}
            className="ml-2 text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
          >
            Switch to {activeMode === 'templates' ? 'Custom' : 'Templates'}
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="transition-all duration-300">
        {activeMode === 'templates' ? (
          <div className="animate-fadeIn">
            <PromptBuilderEnhanced />
          </div>
        ) : (
          <div className="animate-fadeIn">
            <PromptBuilderAdvanced />
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedPromptBuilder;